import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { RequireAuth } from "@/auth/RequireAuth";
import { CallbackPage } from "@/auth/CallbackPage";
import DashboardLayout from "@/components/templates/DashboardLayout";

import HomePage from "@/routes/public/HomePage";
import AboutPage from "@/routes/public/AboutPage";
import PublicCampaignsPage from "@/routes/public/CampaignsPage";
import ContactPage from "@/routes/public/ContactPage";

// Lazy-loaded: the blood_bank dashboard pulls in recharts, jspdf,
// jspdf-autotable, and qr-scanner — none of which a landing-page visitor
// or a donor/hospital-shell visitor needs. Splitting these out brought
// the initial bundle down from one 946 KB / 276 KB-gzip chunk to a small
// shell plus per-route chunks loaded on demand.
const BloodBankHome = lazy(() => import("@/routes/blood_bank/BloodBankHome"));
const BloodBankSettings = lazy(() => import("@/routes/blood_bank/Settings"));
const BloodBankProfile = lazy(() => import("@/routes/blood_bank/Profile"));
const BloodBankEstablishments = lazy(() => import("@/routes/blood_bank/Establishments"));
const Inventory = lazy(() => import("@/routes/blood_bank/inventory/Inventory"));
const InventoryByBloodGroup = lazy(() => import("@/routes/blood_bank/inventory/InventoryByBloodGroup"));
const Forecast = lazy(() => import("@/routes/blood_bank/inventory/Forecast"));
const Donors = lazy(() => import("@/routes/blood_bank/donors/Donors"));
const DonorsList = lazy(() => import("@/routes/blood_bank/donors/DonorsList"));
const DonorSlots = lazy(() => import("@/routes/blood_bank/donors/DonorSlots"));
const DonorAppointment = lazy(() => import("@/routes/blood_bank/donors/DonorAppointment"));
const DonorForm = lazy(() => import("@/routes/blood_bank/donors/DonorForm"));
const AppointmentVitals = lazy(() => import("@/routes/blood_bank/appointments/AppointmentVitals"));
const DonorReports = lazy(() => import("@/routes/blood_bank/donors/reports/DonorReports"));
const ActiveInactiveReport = lazy(() => import("@/routes/blood_bank/donors/reports/ActiveInactiveReport"));
const DonationHistoryReport = lazy(() => import("@/routes/blood_bank/donors/reports/DonationHistoryReport"));
const Campaigns = lazy(() => import("@/routes/blood_bank/campaigns/Campaigns"));
const CampaignRequests = lazy(() => import("@/routes/blood_bank/campaigns/CampaignRequests"));
const CampaignRequestDetail = lazy(() => import("@/routes/blood_bank/campaigns/CampaignRequestDetail"));
const Equipment = lazy(() => import("@/routes/blood_bank/equipment/Equipment"));
const EquipmentCreate = lazy(() => import("@/routes/blood_bank/equipment/EquipmentCreate"));
const EquipmentDetail = lazy(() => import("@/routes/blood_bank/equipment/EquipmentDetail"));
const Requests = lazy(() => import("@/routes/blood_bank/requests/Requests"));
const RequestForm = lazy(() => import("@/routes/blood_bank/requests/RequestForm"));
const RequestDetails = lazy(() => import("@/routes/blood_bank/requests/RequestDetails"));
const RequestTransit = lazy(() => import("@/routes/blood_bank/requests/RequestTransit"));
const RequestSummary = lazy(() => import("@/routes/blood_bank/requests/RequestSummary"));
const TransitList = lazy(() => import("@/routes/blood_bank/requests/TransitList"));
const Test = lazy(() => import("@/routes/blood_bank/test/Test"));
const BloodTestDetail = lazy(() => import("@/routes/blood_bank/test/BloodTestDetail"));
const BloodTypeTest = lazy(() => import("@/routes/blood_bank/test/BloodTypeTest"));
const HemoglobinTest = lazy(() => import("@/routes/blood_bank/test/HemoglobinTest"));
const HivTest = lazy(() => import("@/routes/blood_bank/test/HivTest"));

const DonorDashboard = lazy(() => import("@/routes/donor/DonorDashboard"));
const DonorProfile = lazy(() => import("@/routes/donor/DonorProfile"));
const DonorSettings = lazy(() => import("@/routes/donor/DonorSettings"));
const DonorExplore = lazy(() => import("@/routes/donor/DonorExplore"));
const HospitalDashboard = lazy(() => import("@/routes/hospital/HospitalDashboard"));
const HospitalInventory = lazy(() => import("@/routes/hospital/HospitalInventory"));
const HospitalProfile = lazy(() => import("@/routes/hospital/HospitalProfile"));
const HospitalRequests = lazy(() => import("@/routes/hospital/HospitalRequests"));
const HospitalSettings = lazy(() => import("@/routes/hospital/HospitalSettings"));
const HospitalTest = lazy(() => import("@/routes/hospital/HospitalTest"));
const ItSupportDashboard = lazy(() => import("@/routes/it_support/ItSupportDashboard"));
const ItSupportProfile = lazy(() => import("@/routes/it_support/ItSupportProfile"));
const UserManagement = lazy(() => import("@/routes/it_support/UserManagement"));
const CampaignOrgDashboard = lazy(() => import("@/routes/campaign_org/CampaignOrgDashboard"));
const CampaignOrgCampaign = lazy(() => import("@/routes/campaign_org/CampaignOrgCampaign"));
const CampaignOrgProfile = lazy(() => import("@/routes/campaign_org/CampaignOrgProfile"));

function RouteFallback() {
  return (
    <div className="flex h-full items-center justify-center p-12">
      <p className="text-gray-400 text-sm">Loading…</p>
    </div>
  );
}

export function App() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public */}
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/campaigns" element={<PublicCampaignsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/auth/callback" element={<CallbackPage />} />

        {/* blood_bank — restored to full UI parity with doh_web_frontend (Phase 1) */}
        <Route
          path="/blood_bank"
          element={
            <RequireAuth allow={["BLOOD_BANK"]}>
              <DashboardLayout />
            </RequireAuth>
          }
        >
          <Route index element={<BloodBankHome />} />
          <Route path="settings" element={<BloodBankSettings />} />
          <Route path="profile" element={<BloodBankProfile />} />
          <Route path="establishments" element={<BloodBankEstablishments />} />
          <Route path="inventory" element={<Inventory />} />
          <Route path="inventory/blood_group" element={<InventoryByBloodGroup />} />
          <Route path="inventory/forecast" element={<Forecast />} />
          <Route path="donors" element={<Donors />} />
          <Route path="donors/list" element={<DonorsList />} />
          <Route path="donors/slots" element={<DonorSlots />} />
          <Route path="donors/appointment/:appointmentId" element={<DonorAppointment />} />
          <Route path="donors/form/:appointmentId" element={<DonorForm />} />
          <Route path="donors/reports" element={<DonorReports />} />
          <Route path="donors/reports/active-inactive" element={<ActiveInactiveReport />} />
          <Route path="donors/reports/donation-history" element={<DonationHistoryReport />} />
          <Route path="appointments/:appointmentId/vitals" element={<AppointmentVitals />} />
          <Route path="campaigns" element={<Campaigns />} />
          <Route path="campaigns/requests" element={<CampaignRequests />} />
          <Route path="campaigns/requests/:campaignId" element={<CampaignRequestDetail />} />
          <Route path="equipment" element={<Equipment />} />
          <Route path="equipment/create" element={<EquipmentCreate />} />
          <Route path="equipment/:id" element={<EquipmentDetail />} />
          <Route path="requests" element={<Requests />} />
          <Route path="requests/request_form" element={<RequestForm />} />
          <Route path="requests/request_details" element={<RequestDetails />} />
          <Route path="requests/request_details/transit" element={<RequestTransit />} />
          <Route path="requests/request_details/summary" element={<RequestSummary />} />
          <Route path="requests/transit" element={<TransitList />} />
          <Route path="test" element={<Test />} />
          <Route path="test/blood_test/:bloodId" element={<BloodTestDetail />} />
          <Route path="test/blood_test/:bloodId/blood_type" element={<BloodTypeTest />} />
          <Route path="test/blood_test/:bloodId/hemoglobin" element={<HemoglobinTest />} />
          <Route path="test/blood_test/:bloodId/hiv" element={<HivTest />} />
        </Route>

        {/* donor, hospital, it_support, campaign_org — UI ported to match
            doh_web_frontend in Phase 1, wired to real backend data in
            Phase 2 (see PHASE-2-BACKEND-GAPS.md for what's still mocked). */}
        <Route
          path="/donor"
          element={
            <RequireAuth allow={["DONOR"]}>
              <DashboardLayout />
            </RequireAuth>
          }
        >
          <Route index element={<DonorDashboard />} />
          <Route path="profile" element={<DonorProfile />} />
          <Route path="settings" element={<DonorSettings />} />
          <Route path="explore" element={<DonorExplore />} />
        </Route>

        <Route
          path="/hospital"
          element={
            <RequireAuth allow={["HOSPITAL"]}>
              <DashboardLayout />
            </RequireAuth>
          }
        >
          <Route index element={<HospitalDashboard />} />
          <Route path="inventory" element={<HospitalInventory />} />
          <Route path="profile" element={<HospitalProfile />} />
          <Route path="requests" element={<HospitalRequests />} />
          <Route path="settings" element={<HospitalSettings />} />
          <Route path="test" element={<HospitalTest />} />
        </Route>

        <Route
          path="/it_support"
          element={
            <RequireAuth allow={["IT_SUPPORT"]}>
              <DashboardLayout />
            </RequireAuth>
          }
        >
          <Route index element={<ItSupportDashboard />} />
          <Route path="profile" element={<ItSupportProfile />} />
          <Route path="user_management" element={<UserManagement />} />
        </Route>

        <Route
          path="/campaign_org"
          element={
            <RequireAuth allow={["CAMP_ORGANIZER"]}>
              <DashboardLayout />
            </RequireAuth>
          }
        >
          <Route index element={<CampaignOrgDashboard />} />
          <Route path="campaign" element={<CampaignOrgCampaign />} />
          <Route path="profile" element={<CampaignOrgProfile />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
